import { useState } from 'react'

import { ApiFundOfFundsPortfolio, ApiModelPortfolio, ApiSelfSelectPortfolio } from '@/api'
import {
  FieldError,
  Icon,
  IconButton,
  Input,
  Label,
  LinkButton,
  RadioTab,
  Space,
  Typography
} from '@/components/core-v2'
import { PORTFOLIO_TYPE } from '@/constants'
import { useUser } from '@/utilities/hooks/useUser'

interface ProposedChangesApprovalProps {
  canProceedApproval: boolean
  onApprovePortfolio: () => void
  onCancel: () => void
  onChangeApprovalStatus: (status: 'Approved' | 'Rejected' | '') => void
  onChangeApprovalUserName: (userName: string) => void
  portfolio: (ApiModelPortfolio | ApiFundOfFundsPortfolio | ApiSelfSelectPortfolio) | null
}

export const ProposedChangesApproval = ({
  canProceedApproval,
  onApprovePortfolio,
  onCancel,
  onChangeApprovalStatus,
  onChangeApprovalUserName,
  portfolio
}: ProposedChangesApprovalProps) => {
  const [approvalOption, setApprovalOption] = useState<'Approved' | 'Rejected'>()
  const [approvalUserName, setApprovalUserName] = useState('')
  const [validationError, setValidationError] = useState<string>('')

  const { type } = portfolio

  const {
    data: { userName }
  } = useUser()

  const isAllowed = () => {
    const isValid = approvalUserName === userName
    if (!isValid) {
      setValidationError(`Entered user name does not match with your username: ${userName}`)
      return false
    }
    return true
  }

  const handleChangeApprovalStatus = (status: 'Approved' | 'Rejected') => {
    setApprovalOption(status)
    onChangeApprovalStatus(status)
  }

  const handleChangeApprovalUserName = (userName: string) => {
    setApprovalUserName(userName)
    onChangeApprovalUserName(userName)
  }

  const handleApprovePortfolio = () => {
    if (isAllowed()) {
      onApprovePortfolio()
    }
  }

  return (
    <Space pb={6} px={6}>
      <Space isflex justifyContent="flex-start" pt={5}>
        <Space mr={4}>
          <RadioTab
            isSelected={approvalOption === 'Approved'}
            onClick={() => handleChangeApprovalStatus('Approved')}
          >
            {`Approve this ${PORTFOLIO_TYPE[type].value}`}
          </RadioTab>
        </Space>
        <Space mr={4}>
          <RadioTab
            isSelected={approvalOption === 'Rejected'}
            onClick={() => handleChangeApprovalStatus('Rejected')}
          >
            {`Reject this ${PORTFOLIO_TYPE[type].value}`}
          </RadioTab>
        </Space>
      </Space>
      <Space mb={3}>
        <Label htmlFor="approve-changes-username">
          <Space mb={3}>
            <Typography fontSize="13px" fontWeight="normal" variant="b3">
              Please enter your username *
            </Typography>
          </Space>
        </Label>
        <Input
          id="approve-changes-username"
          type="text"
          value={approvalUserName}
          onInput={x => handleChangeApprovalUserName(x.currentTarget.value)}
        />
        {validationError && (
          <FieldError id="approve-changes-incorrect-username">{validationError}</FieldError>
        )}
      </Space>
      <Space isflex justifyContent="space-between">
        <Space isflex alignItems="center">
          <Space mr={2}>
            <Icon color="var(--Red-Dark)" name="chevronLeft" size="small" />
          </Space>
          <LinkButton type="button" onClick={onCancel}>
            Cancel and return
          </LinkButton>
        </Space>
        <IconButton
          disabled={!canProceedApproval}
          icon={<Icon name="check" size="small" />}
          iconPosition="left"
          operationType="progress"
          text="Submit"
          type="submit"
          onClick={handleApprovePortfolio}
        />
      </Space>
    </Space>
  )
}
